import { countries } from '@/consts/languageAccordance';
import {
    ThemeVariables,
    variables as defaultVariables,
    colors as defaultColors,
} from '../mpanels/variables';
import { addOpacityToThemeColor } from '@/utils/styles/addOpacity';

const colors = {
    ...defaultColors,
    white: 'rgba(255, 255, 255, 1)',
    primary: {
        '100': 'rgba(114, 169, 224, 1)',
        '200': 'rgba(86, 140, 194, 1)',
        '300': 'rgba(57, 110, 163, 1)',
        '400': 'rgba(29, 81, 133, 1)',
        '500': 'rgba(0, 51, 102, 1)',
        '600': 'rgba(0, 41, 82, 1)',
        '700': 'rgba(0, 31, 61, 1)',
        '800': 'rgba(0, 20, 41, 1)',
        '900': 'rgba(0, 10, 20, 1)',
    },
    secondary: {
        '100': 'rgba(228, 243, 217, 1)',
        '200': 'rgba(202, 230, 180, 1)',
        '300': 'rgba(175, 218, 142, 1)',
        '400': 'rgba(149, 205, 105, 1)',
        '500': 'rgba(122, 193, 67, 1)',
        '600': 'rgba(98, 154, 54, 1)',
        '700': 'rgba(73, 116, 40, 1)',
        '800': 'rgba(49, 77, 27, 1)',
        '900': 'rgba(24, 39, 13, 1)',
    },
    tertiary: {
        '100': 'rgba(189, 224, 243, 1)',
        '200': 'rgba(142, 200, 231, 1)',
        '300': 'rgba(94, 176, 219, 1)',
        '400': 'rgba(47, 152, 207, 1)',
        '500': 'rgba(0, 128, 195, 1)',
        '600': 'rgba(0, 102, 156, 1)',
        '700': 'rgba(0, 77, 117, 1)',
        '800': 'rgba(0, 51, 78, 1)',
        '900': 'rgba(0, 26, 39, 1)',
    },
    neutral: {
        '50': 'rgba(245, 246, 247, 1)',
        '100': 'rgba(240, 240, 240, 1)',
        '200': 'rgba(225, 225, 225, 1)',
        '300': 'rgba(210, 210, 210, 1)',
        '400': 'rgba(181, 181, 181, 1)',
        '500': 'rgba(140, 140, 140, 1)',
        '600': 'rgba(120, 120, 120, 1)',
        '700': 'rgba(92, 94, 97, 1)',
        '800': 'rgba(59, 59, 59, 1)',
        '900': 'rgba(13, 13, 13, 1)',
    },
    success: 'rgba(15, 119, 39, 1)',
    info: 'rgba(6, 87, 168, 1)',
    warning: 'rgba(242, 141, 0, 1)',
    error: 'rgba(142, 19, 32, 1)',
};

const palette = {
    ...defaultVariables.colors.palette,
    primary: {
        ...colors.primary,
        main: colors.primary['500'],
        contrastText: colors.white,
    },
    secondary: {
        ...colors.secondary,
        main: colors.secondary['500'],
        contrastText: colors.white,
    },
    tertiary: {
        ...colors.tertiary,
        main: colors.tertiary['500'],
        contrastText: colors.white,
    },
    neutral: colors.neutral,
    background: {
        default: colors.white,
    },
    text: {
        primary: colors.neutral['800'],
    },
};

const fonts = {
    primary: '"Aptos", sans-serif',
    secondary: '"Libre Franklin", serif',
    additional: '"Libre Franklin", serif',
    body: {
        fontSize: '1rem',
    },
};

type M3GRThemeVariables = ThemeVariables & {
    colors: {
        palette: ThemeVariables['colors']['palette'] & typeof palette;
    };
};

export const variables: M3GRThemeVariables = {
    themeName: 'm3gr',
    sortOrder: {
        ...defaultVariables.sortOrder,
        countriesSelectOptions: [
            [
                String(countries['at'].phraseId),
                String(countries['au'].phraseId),
                String(countries['be'].phraseId),
                String(countries['br'].phraseId),
                String(countries['ca'].phraseId),
                String(countries['dk'].phraseId),
                String(countries['fi'].phraseId),
                String(countries['fr'].phraseId),
                String(countries['de'].phraseId),
                String(countries['gb'].phraseId),
                String(countries['in'].phraseId),
                String(countries['it'].phraseId),
                String(countries['nl'].phraseId),
                String(countries['no'].phraseId),
                String(countries['nz'].phraseId),
                String(countries['es'].phraseId),
                String(countries['se'].phraseId),
                String(countries['us'].phraseId),
                String(countries['ch'].phraseId),
            ],
            ['divider'],
            ['*'],
        ],
    },
    colors: {
        palette,
        links: {
            main: palette.primary.main,
            hover: colors.primary['300'],
            active: colors.primary['700'],
            disabled: palette.primary.main,
        },
        error: {
            main: colors.error,
        },
        other: {
            badge: {
                topMenu: {
                    color: palette.background.default,
                    background: 'rgba(242, 141, 0, 1)',
                },
            },
            chip: {
                default: {
                    background: 'rgba(235, 245, 250, 1)',
                    text: palette.tertiary['700'],
                },
                methodologyType: {
                    telephone: {
                        background: 'rgba(235, 245, 250, 1)',
                        text: palette.tertiary['700'],
                    },
                    online: {
                        background: 'rgba(235, 245, 250, 1)',
                        text: palette.tertiary['700'],
                    },
                    inPerson: {
                        background: 'rgba(235, 245, 250, 1)',
                        text: palette.tertiary['700'],
                    },
                },
            },
            table: {
                evenRowHighlight: colors.neutral['50'],
                hoverHighlight: colors.neutral['100'],
                headerText: colors.neutral['500'],
                text: palette.text.primary,
                hoverText: palette.text.primary,
            },
            background: {
                ...defaultVariables.colors.other.background,
                header: 'white',
                topMenu: 'white',
                footer: palette.neutral['50'],
                highlight: addOpacityToThemeColor(palette.primary.main, 0.1),
                highlightHover: 'rgba(255, 255, 255, 0.10)',
                success: addOpacityToThemeColor(colors.success, 0.1),
                warning: addOpacityToThemeColor(colors.warning, 0.1),
                info: addOpacityToThemeColor(colors.info, 0.1),
                error: addOpacityToThemeColor(colors.error, 0.1),
                divideline: addOpacityToThemeColor(colors.black, 0.16),
                preloader: palette.tertiary.main,
            },
            text: {
                ...defaultVariables.colors.other.text,
                header: palette.primary.main,
                registrationDescription: palette.background.default,
                footer: colors.neutral['700'],
                copyrightText: colors.neutral['700'],
                lightGray: colors.neutral['100'],
                gray: colors.neutral['400'],
                success: colors.success,
                warning: colors.warning,
                info: colors.info,
                error: colors.error,
            },
            inputs: {
                default: colors.neutral['400'],
                hover: colors.primary['300'],
                focus: colors.primary['300'],
            },
            icon: {
                stroke: {
                    primary: palette.primary.main,
                    secondary: palette.secondary.main,
                    tertiary: palette.tertiary.main,
                },
                background: {
                    primary: 'transparent',
                    secondary: 'transparent',
                    tertiary: 'transparent',
                },
            },
            notifications: {
                success: colors.success,
                warning: colors.warning,
                info: colors.info,
                error: colors.error,
            },
        },
    },
    fonts,
    borderRadius: {
        primary: '4px',
        secondary: '0',
        tertiary: '0',
        topMenu: '0',
        mobileTopMenu: '0 0 8px 8px',
    },
    padding: {
        ...defaultVariables.padding,
        inputsDefault: '8px 12px',
        tableCellPadding: '8px 16px',
        elementPadding: '0 0 12px 0',
    },
    margin: {
        ...defaultVariables.margin,
    },
    boxShadows: {
        popoverShadow: `none`,
        tableShadow: 'none',
        tableSelectedShadow: `inset 0 2px 4px ${addOpacityToThemeColor(colors.black, 0.06)}`,
        headerShadow: `0 1px 0 ${colors.neutral['200']}`,
        buttonShadow: 'none',
        inputShadow: `0px 0px 0px 2px ${addOpacityToThemeColor(palette.primary['300'], 0.5)}`,
        tooltipShadow: '0px 4px 4px 0px rgba(218, 218, 218, 0.25)',
        descriptiveShadow: 'initial',
    },
    layout: {
        helpCentreInsteadFaq: true,
        home: {
            hasBackgroundImage: 'centerTop',
            pageTitle: {
                textAlign: 'left',
                position: 'top',
            },
            description: {
                visible: true,
                position: 'top',
            },
            picBlock: {
                visible: false,
                position: 'top',
            },
            iconsBlock: {
                visible: true,
                position: 'bottom',
                inlineIcons: false,
                paperBox: false,
            },
            registrationForm: {
                fullwidthForm: false,
                showStepTitle: false,
                stepperOrientation: 'horizontal',
            },
        },
        footer: {
            fullWidth: true,
            logoPosition: 'right',
            themeColorSocialIcons: false,
            companyLinkVisible: false,
            inlineFollowUs: true,
            hasFollowUsTitle: false,
        },
    },
    cookie: {
        button: {
            hover: {
                color: 'white',
                backgroundColor: palette.primary['300'],
            },
            borderRadius: '4px',
            shadow: 'initial',
        },
        toggle: {
            initial: {
                selected: {
                    toggleColor: palette.primary.main,
                    backgroundColor: addOpacityToThemeColor(palette.primary.main, 0.5),
                },
                unselected: {
                    toggleColor: 'white',
                    backgroundColor: palette.neutral['300'],
                },
            },
            disabled: {
                toggleColor: palette.neutral['100'],
                backgroundColor: palette.neutral['300'],
            },
        },
    },
    typography: {
        fontFamily: fonts.secondary,
        h1: {
            color: palette.primary.main,
            fontFamily: fonts.primary,
            fontSize: '2.375rem', // 38px
            fontWeight: '800',
        },
        h2: {
            color: palette.primary.main,
            fontFamily: fonts.primary,
            fontSize: '2rem', // 32px
            fontWeight: '800',
        },
        h3: {
            color: palette.primary.main,
            fontFamily: fonts.primary,
            fontSize: '1.5rem', // 24px
            fontWeight: '800',
        },
        h4: {
            color: palette.primary.main,
            fontFamily: fonts.primary,
            fontSize: '1.25rem', // 20px
            fontWeight: '800',
        },
        h5: {
            color: palette.text.primary,
            fontFamily: fonts.primary,
            fontSize: '1.125rem', // 18px
            fontWeight: '800',
        },
        h6: {
            color: palette.text.primary,
            fontFamily: fonts.primary,
            fontSize: '1rem', // 16px
            fontWeight: '800',
        },
        body1: {
            fontSize: '1rem', // 16px
            fontFamily: fonts.secondary,
            fontWeight: '400',
        },
        body2: {
            fontSize: '0.875rem', // 14px
            fontFamily: fonts.secondary,
            fontWeight: '400',
        },
        subtitle1: {
            fontSize: '1rem', // 16px
            fontFamily: fonts.secondary,
        },
        subtitle2: {
            fontSize: '0.875rem', // 14px
            fontFamily: fonts.secondary,
        },
        subtitle3: {
            fontSize: '0.75rem', // 12px
            fontFamily: fonts.secondary,
        },
        greyLabel: {
            fontSize: '0.75rem', // 12px
            fontFamily: fonts.secondary,
        },
        link: {
            textDecoration: 'none',
            '&:hover': {
                textDecoration: 'underline',
            },
            '&:active': {
                textDecoration: 'inherit',
            },
            '&:disabled': {
                textDecoration: 'inherit',
            },
        },
    },
};
